import {DateTimeValue} from '../../../../api/models/date-time-value';
import {DebugTableRow} from './debug-status-table.helper';

export interface ExportStatusLike {
  deviceNames?: Record<string, string>;
  deviceTimeValues?: Record<string, DateTimeValue[]>;
  timeBucket?: string;
}

export interface ExportNodeTable {
  id: string;
  title: string;
  rows: DebugTableRow[];
}

export interface ExportTableSection {
  key: string;
  label: string;
  tables: ExportNodeTable[];
}

interface ExportResponseLike {
  values?: Record<string, unknown>;
}

export function buildEnergyDebugExportSections(response: Record<string, unknown> | null): ExportTableSection[] {
  if (!response) {
    return [];
  }

  const statuses = getExportStatuses(response);

  return Object.entries(statuses)
    .map(([fieldKey, value]) => {
      const status = asExportStatus(value);
      if (!status) {
        return null;
      }

      const deviceNames = status.deviceNames ?? {};
      const deviceTimeValues = status.deviceTimeValues ?? {};
      const nodeIds = Array.from(new Set([...Object.keys(deviceNames), ...Object.keys(deviceTimeValues)]));

      const tables = nodeIds.map((nodeId, index) => {
        const nodeName = deviceNames[nodeId]?.trim() || `Node ${index + 1}`;
        const rawTimeValues = deviceTimeValues[nodeId];
        const rows = createExportRows(Array.isArray(rawTimeValues) ? rawTimeValues : []);
        return {
          id: `${fieldKey}-${nodeId || index}`,
          title: nodeName,
          rows,
        };
      });

      return {
        key: fieldKey,
        label: fieldKey,
        tables: tables.filter((table) => table.rows.length > 0),
      };
    })
    .filter((section): section is ExportTableSection => !!section && section.tables.length > 0);
}

export function buildEnergyDebugExportRequestUrl(
  rootUrl: string,
  locationId: string,
  from: string,
  to: string,
  aggregation: string,
): string {
  const normalizedRootUrl = rootUrl.replace(/\/$/, '');
  const queryParams = new URLSearchParams({
    aggregation,
    from,
    to,
  });

  return `${normalizedRootUrl}/locations/${encodeURIComponent(locationId)}/energy-debug-export?${queryParams.toString()}`;
}

function getExportStatuses(response: Record<string, unknown>): Record<string, unknown> {
  const values = (response as ExportResponseLike).values;
  if (values && typeof values === 'object') {
    return values;
  }

  return response;
}

function asExportStatus(value: unknown): ExportStatusLike | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as ExportStatusLike;
  if (!candidate.deviceTimeValues || typeof candidate.deviceTimeValues !== 'object') {
    return null;
  }

  return candidate;
}

function createExportRows(timeValues: DateTimeValue[]): DebugTableRow[] {
  return [...timeValues]
    .map((item) => ({
      timestamp: item.read ?? '',
      value: item.value,
    }))
    .sort((a, b) => toTime(a.timestamp) - toTime(b.timestamp));
}

function toTime(timestamp: string | undefined): number {
  if (!timestamp) {
    return Number.MAX_SAFE_INTEGER;
  }

  const time = new Date(timestamp).getTime();
  return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
}
